import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Me } from "../../api/types";
import { S } from "../../styles";
import { MiniScoreBar } from "../../components/MiniScoreBar";
import { StatCard } from "../../components/StatCard";
import { HBar } from "../../components/HBar";
import { LoadingState, ErrorState } from "../../components/QueryStates";

export function AnalyticsPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(90);

  const canRecompute =
    !!me && (me.roles.includes("admin") || me.roles.includes("manager"));

  const { data, isLoading, error } = useQuery({
    queryKey: ["analytics", days],
    queryFn: () =>
      Promise.all([
        api.getAnalyticsOverview(days),
        api.getSpendBySupplier(days, 10),
        api.getRFQMetrics(days),
        api.getAOGMetrics(days),
        api.getSavings(days),
        api.getSupplierScorecards(),
      ]).then(([ov, sp, rfqM, aogM, savM, sc]) => ({
        overview: ov,
        spend: sp.by_supplier,
        rfq: rfqM,
        aog: aogM,
        savings: savM,
        scorecards: sc.scorecards,
      })),
    staleTime: 60_000,
  });

  const recomputeMutation = useMutation({
    mutationFn: api.recomputeScorecards,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["analytics", days] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: api.runAnalyticsETL,
    onSuccess: () => {
      // The ETL runs asynchronously in Temporal; give it a moment to populate
      // the ClickHouse fact tables, then refetch so the new data appears.
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ["analytics", days] });
      }, 4000);
    },
  });

  const scoreColor = (s: number) =>
    s >= 0.8 ? "#2f9e44" : s >= 0.5 ? "#e67700" : "#c92a2a";

  const maxSpend =
    data && data.spend.length > 0
      ? Math.max(...data.spend.map((s) => s.total_spend))
      : 1;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>📊 Analytics & Performance</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            style={{
              ...S.select,
              width: "auto",
              marginBottom: 0,
              fontSize: 13,
            }}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button
            style={S.btnSm}
            onClick={() =>
              void queryClient.invalidateQueries({
                queryKey: ["analytics", days],
              })
            }
          >
            ↺ Refresh
          </button>
          {canRecompute && (
            <button
              style={{ ...S.btnSm, background: "#0891b2" }}
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              title="Force an immediate PostgreSQL → analytics sync"
            >
              {syncMutation.isPending ? "Syncing…" : "⟲ Sync Data"}
            </button>
          )}
          {canRecompute && (
            <button
              style={{ ...S.btnSm, background: "#7c3aed" }}
              onClick={() => recomputeMutation.mutate()}
              disabled={recomputeMutation.isPending}
            >
              {recomputeMutation.isPending
                ? "Recomputing…"
                : "⟳ Recompute Scorecards"}
            </button>
          )}
        </div>
      </div>

      {syncMutation.isSuccess && (
        <p style={S.ok}>{syncMutation.data.message}</p>
      )}
      {syncMutation.error && <p style={S.err}>{syncMutation.error.message}</p>}
      {recomputeMutation.isSuccess && (
        <p style={S.ok}>{recomputeMutation.data.message}</p>
      )}
      {recomputeMutation.error && (
        <p style={S.err}>{recomputeMutation.error.message}</p>
      )}

      {error && <ErrorState error={error} />}
      {isLoading && <LoadingState label="Loading analytics…" />}

      {!isLoading && data && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <StatCard
              label="Total Spend"
              value={`$${(data.overview.total_spend_usd / 1000).toFixed(1)}k`}
              sub={`${data.overview.po_count} POs`}
              color="#4f46e5"
            />
            <StatCard
              label="RFQ Conversion"
              value={`${Math.round(data.overview.rfq_conversion_rate * 100)}%`}
              sub={`${data.overview.rfq_quoted} of ${data.overview.rfq_total} quoted`}
              color={
                data.overview.rfq_conversion_rate >= 0.7 ? "#2f9e44" : "#e67700"
              }
            />
            <StatCard
              label="Avg Savings vs Baseline"
              value={`${data.overview.avg_savings_pct < 0 ? "" : "+"}${data.overview.avg_savings_pct.toFixed(1)}%`}
              sub={
                data.overview.avg_savings_pct < 0
                  ? "Below market — good"
                  : "Above market"
              }
              color={data.overview.avg_savings_pct < 0 ? "#2f9e44" : "#c92a2a"}
            />
            <StatCard
              label="AOG Requirements"
              value={`${data.aog?.total_aog_requirements ?? 0}`}
              sub={
                data.aog && data.aog.avg_first_quote_hours > 0
                  ? `Avg ${data.aog.avg_first_quote_hours.toFixed(1)}h to first quote`
                  : "No AOG data"
              }
              color="#c92a2a"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div style={S.card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ ...S.h3, margin: 0 }}>
                  Spend by Supplier (top 10)
                </h3>
                <button
                  style={{ ...S.btnGhost, fontSize: 11 }}
                  onClick={() => api.exportCSV("spend", days)}
                >
                  ⬇ CSV
                </button>
              </div>
              {data.spend.length === 0 ? (
                <p style={S.muted}>No purchase orders in this period.</p>
              ) : (
                data.spend.map((s) => (
                  <HBar
                    key={s.supplier_id}
                    label={s.supplier_name}
                    value={s.total_spend}
                    max={maxSpend}
                    currency="$"
                  />
                ))
              )}
            </div>

            {data.rfq && (
              <div style={S.card}>
                <h3 style={{ ...S.h3, margin: "0 0 12px" }}>RFQ Funnel</h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <StatCard label="Total RFQs" value={`${data.rfq.total}`} />
                  <StatCard
                    label="Quoted"
                    value={`${data.rfq.quoted}`}
                    color="#2f9e44"
                  />
                  <StatCard
                    label="Expired"
                    value={`${data.rfq.expired}`}
                    color="#e67700"
                  />
                  <StatCard
                    label="In Progress"
                    value={`${data.rfq.in_progress}`}
                    color="#4f46e5"
                  />
                </div>
                <div style={{ ...S.muted, fontSize: 12 }}>
                  <div>
                    Avg dispatch time:{" "}
                    <strong>{data.rfq.avg_dispatch_hours.toFixed(1)}h</strong>
                  </div>
                  <div>
                    Avg response time:{" "}
                    <strong>{data.rfq.avg_response_hours.toFixed(1)}h</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          {data.savings && data.savings.approved_recommendations > 0 && (
            <div
              style={{
                ...S.card,
                marginBottom: 16,
                background: "#f0fff4",
                border: "1px solid #2f9e4444",
              }}
            >
              <h3 style={{ ...S.h3, color: "#2f9e44", margin: "0 0 8px" }}>
                Savings vs Market Baseline
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 12,
                }}
              >
                <StatCard
                  label="Approved Recs"
                  value={`${data.savings.approved_recommendations}`}
                />
                <StatCard
                  label="Avg Price vs Baseline"
                  value={`${data.savings.avg_savings_pct < 0 ? "" : "+"}${data.savings.avg_savings_pct.toFixed(1)}%`}
                  color={
                    data.savings.avg_savings_pct < 0 ? "#2f9e44" : "#c92a2a"
                  }
                />
                <StatCard
                  label="Est. Savings"
                  value={`$${data.savings.estimated_savings_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  color="#2f9e44"
                />
              </div>
            </div>
          )}

          <div style={S.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ ...S.h3, margin: 0 }}>Supplier Scorecards</h3>
              <button
                style={{ ...S.btnGhost, fontSize: 11 }}
                onClick={() => api.exportCSV("scorecards", days)}
              >
                ⬇ CSV
              </button>
            </div>
            {data.scorecards.length === 0 ? (
              <p style={S.muted}>
                No scorecards yet.{" "}
                {canRecompute
                  ? 'Click "Recompute Scorecards" to generate them.'
                  : "Ask a manager or admin to recompute scorecards."}
              </p>
            ) : (
              <div style={{ overflowX: "auto" as const }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f4f5f9",
                        borderBottom: "2px solid #e8eaf0",
                      }}
                    >
                      {[
                        "Supplier",
                        "Country",
                        "Responsiveness",
                        "Price",
                        "Delivery",
                        "Overall",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "7px 10px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#555",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.scorecards.map((s, i) => (
                      <tr
                        key={s.supplier_id}
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          background: i % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>
                          {s.supplier_name}
                        </td>
                        <td style={{ padding: "8px 10px", color: "#666" }}>
                          {s.country ?? "—"}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {s.scorecard_updated_at ? (
                            <>
                              <div style={{ fontSize: 11, color: "#666" }}>
                                {s.responsiveness_hours.toFixed(1)}h avg
                              </div>
                              <MiniScoreBar
                                score={Math.max(
                                  0,
                                  1 - s.responsiveness_hours / 48,
                                )}
                              />
                            </>
                          ) : (
                            <span style={{ color: "#aaa" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {s.price_competitiveness > 0 ? (
                            <>
                              <div style={{ fontSize: 11, color: "#666" }}>
                                {Math.round(s.price_competitiveness * 100)}%
                              </div>
                              <MiniScoreBar score={s.price_competitiveness} />
                            </>
                          ) : (
                            <span style={{ color: "#aaa" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {s.on_time_delivery_rate > 0 ? (
                            <>
                              <div style={{ fontSize: 11, color: "#666" }}>
                                {Math.round(s.on_time_delivery_rate * 100)}%
                              </div>
                              <MiniScoreBar score={s.on_time_delivery_rate} />
                            </>
                          ) : (
                            <span style={{ color: "#aaa" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: scoreColor(s.score_overall),
                            }}
                          >
                            {s.score_overall > 0
                              ? Math.round(s.score_overall * 100)
                              : "—"}
                            {s.score_overall > 0 ? "%" : ""}
                          </span>
                          {!s.scorecard_updated_at && (
                            <div style={{ ...S.muted, fontSize: 10 }}>
                              not computed
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
